import React, { useState } from "react";
import { countryData as Countries } from "../../../utils/countries";

interface Country {
  name: string;
  iso: string;
  countryCode: string;
}

interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  defaultCountry?: string;
  required?: boolean;
}

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  defaultCountry = "AF",
  required,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    Countries.find((country) => country.iso === defaultCountry) || Countries[0]
  );
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [rawInput, setRawInput] = useState("");

  const filteredCountries = Countries.filter((country) =>
    country.name.toLowerCase().includes(search.toLowerCase())
  );

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // ---- Only allow numbers visually
    const typed = e.target.value.replace(/\D/g, "");
    setRawInput(typed);

    let cleaned = typed;
    if (cleaned.startsWith("0")) cleaned = cleaned.slice(1);

    const full = `${selectedCountry.countryCode}${cleaned}`;
    onChange(full);
  };


const handleCountrySelect = (country: Country) => {
  setSelectedCountry(country);
  setSearch("");
  setShowDropdown(false);

  // ---- Extract only the digits that come after the old country code
  const oldCountryDigits = selectedCountry.countryCode;
  const currentNumber = value.startsWith(oldCountryDigits)
    ? value.slice(oldCountryDigits.length)
    : value;

  // Combine with new code
  onChange(`${country.countryCode}${currentNumber}`);
};


  return (
    <div className="relative w-full">
      <div className="flex border rounded-lg overflow-hidden">
        <button
          type="button"
          className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <span className="font-medium dark:text-white/90">+{selectedCountry.countryCode}</span>
        </button>

        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          className="flex-1 px-3 py-2 outline-none dark:text-white/90"
          placeholder="Enter phone number"
          value={rawInput}
          onChange={handlePhoneChange}
          required={required}
        />
      </div>

      {showDropdown && (
        <div className="absolute z-50 bg-white border mt-2 w-full max-h-64 overflow-y-auto rounded-lg shadow-lg">
          <div className="p-2 sticky top-0 bg-white border-b">
            <input
              type="text"
              placeholder="Search country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border rounded px-2 py-1"
            />
          </div>

          {filteredCountries.map((country) => (
            <div
              key={country.iso}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleCountrySelect(country)}
            >
              <span className="flex-1">{country.name}</span>
              <span className="text-sm text-gray-600">
                +{country.countryCode}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhoneNumberInput;
